<template>
	<v-dialog v-model="dialogOpen" :attach="overlayTarget">
		<template v-slot:activator="{ props: activatorProps }">
			<v-text-field :model-value="displayValue" v-bind="activatorProps" density="compact" variant="outlined" :label="label" readonly></v-text-field>
		</template>

		<template v-slot:default>
			<v-card>
				<v-card-title class="headline">
					<md-icon>filter_alt</md-icon>
					{{ label }}
				</v-card-title>
				<v-sheet class="pa-2">
					<v-text-field v-model="search" clear-icon="cancel" :label="t('Search')" density="compact" variant="outlined" clearable flat hide-details></v-text-field>
				</v-sheet>
				<v-treeview v-model="tempSelectItems" :items="treeItems" :search="search" item-value="id" select-strategy="independent" expand-icon="keyboard_arrow_right" collapse-icon="keyboard_arrow_down" selectable>
					<template v-slot:toggle="{ props: toggleProps, isOpen, isSelected, isIndeterminate, path, item }">
						<v-badge :color="isSelected ? 'success' : 'primary'" :model-value="isSelected || isIndeterminate">
							<template v-slot:badge>
								<md-icon v-if="isSelected">check</md-icon>
								<span v-if="isIndeterminate">{{ selectionsInfo(path) }}</span>
							</template>
							<v-btn v-bind="toggleProps" :color="isIndeterminate ? 'primary' : isSelected ? 'success' : 'medium-emphasis'" :variant="isOpen ? 'outlined' : 'tonal'"></v-btn>
						</v-badge>
					</template>
				</v-treeview>

				<v-card-actions>
					<v-btn :text="t('Clear')" variant="text" color="error" @click="tempSelectItems = []"></v-btn>
					<v-btn :text="t('Cancel')" variant="text" @click="cancel"></v-btn>
					<v-btn :text="t('Valid')" color="primary" variant="tonal" @click="confirm"></v-btn>
				</v-card-actions>
			</v-card>
		</template>
	</v-dialog>
</template>

<script setup>
// #region Imports

import { ref, shallowRef, inject, computed, watch } from 'vue';
import { useTranslator } from '@/utils/useTranslator';

// #endregion

// #region Refs & Vars

const props = defineProps({
	label: { type: String, default: '' },
	items: { type: Array, default: () => [] },
	titleKey: { type: String, default: 'name' },
	titleKeyTranslate: { type: Boolean, default: false },
	multiple: { type: Boolean, default: false },
	required: { type: Boolean, default: false },
});

const t = useTranslator();
const overlayTarget = inject('overlayTarget', null);
const search = shallowRef(null);
const dialogOpen = ref(false);

// #endregion

// #region Filters Data

const selectItems = ref([]);
const tempSelectItems = ref([]);

const treeItems = computed(() => {
	const stripEmptyChildren = (nodes) =>
		nodes.map((node) => {
			if (!node.children || node.children.length === 0) {
				const { children, ...rest } = node;
				return rest;
			}
			return { ...node, children: stripEmptyChildren(node.children) };
		});
	return stripEmptyChildren(props.items);
});

const allFlatItems = computed(() => {
	const flatten = (items) => items.flatMap((item) => [item, ...(item.children ? flatten(item.children) : [])]);
	return flatten(props.items);
});

const displayValue = computed(() => {
	if (!selectItems.value.length) return '';
	return `${selectItems.value.length} ${t('Selected')}`;
});

const filterIsDirty = computed(() => {
	if (props.multiple) return selectItems.value.length > 0 && selectItems.value.length !== props.items.length;
	return selectItems.value.length > 0;
});

function selectionsInfo(path) {
	const node = path.reduce((current, i) => current.children[i], { children: treeItems.value });
	const allLeafsIds = (function flatten(n) {
		return n.children?.flatMap(flatten) ?? [n.id];
	})(node);
	const selectedCount = allLeafsIds.filter((id) => tempSelectItems.value.includes(id)).length;
	return `${selectedCount}`;
}

// #endregion

// #region Methods

const normalizeSelection = (items) => (Array.isArray(items) ? [...items] : []);

function cancel() {
	dialogOpen.value = false;
}

function confirm() {
	selectItems.value = normalizeSelection(tempSelectItems.value);
	dialogOpen.value = false;
}

watch(dialogOpen, (isOpen) => {
	if (!isOpen) return;
	tempSelectItems.value = normalizeSelection(selectItems.value);
});

defineExpose({
	getValues() {
		return selectItems.value.length > 0 ? selectItems.value : null;
	},
	getFullValues() {
		return allFlatItems.value.filter((item) => selectItems.value.includes(item.id));
	},
	isDirty() {
		return filterIsDirty.value;
	},
	isValid() {
		if (!props.required) return true;
		if (props.multiple) return Array.isArray(selectItems.value) && selectItems.value.length > 0;
		return selectItems.value.length > 0;
	},
	isRequired() {
		return props.required;
	},
	selectItems(items) {
		const normalizedItems = normalizeSelection(items);
		selectItems.value = normalizedItems;
		tempSelectItems.value = normalizedItems;
	},
	reset() {
		selectItems.value = [];
		tempSelectItems.value = [];
	},
});

// #endregion
</script>
